/**
 * Sign-in PIN helpers (ADR-0035), pure so they are unit-tested: the format rules the server
 * enforces, readable errors, the activity list and the security notices pushed to signed-in
 * devices. Nothing here stores or logs a PIN.
 */

export const PIN_MIN_LENGTH = 6;
export const PIN_MAX_LENGTH = 12;

/** Why `pin` cannot be set, or null. Mirrors `crates/api/src/pin.rs::validate`. */
export function pinFormatError(pin: string): string | null {
  if (pin.length < PIN_MIN_LENGTH || pin.length > PIN_MAX_LENGTH || !/^[0-9]+$/.test(pin)) {
    return "The PIN must be 6 to 12 digits.";
  }
  const d = Array.from({ length: pin.length }, (_, i) => pin.charCodeAt(i) - 48);
  if (d.every((x) => x === d[0])) return "Choose a PIN that is not one digit repeated.";
  const steps = d.slice(1).map((x, i) => x - (d[i] ?? 0));
  if (steps.every((s) => s === 1) || steps.every((s) => s === -1)) {
    return "Choose a PIN that is not a straight run like 123456.";
  }
  return null;
}

/** The fields of an API error these helpers read (core's `ApiError` has them). */
export type ErrorLike = { status?: number; code?: string; message?: string };

function asError(e: unknown): ErrorLike {
  return typeof e === "object" && e !== null ? e : {};
}

/** A readable message for a failed PIN sign-in. */
export function pinSignInError(e: unknown): string {
  const err = asError(e);
  if (err.status === 429) {
    return "Too many PIN attempts from this network or for this email. Wait up to an hour, then try again.";
  }
  if (err.code === "PIN_REFUSED" || err.status === 401) {
    return "Email or PIN not accepted. After 5 wrong PINs, PIN sign-in is locked for a while (15 min, then longer).";
  }
  return err.message ?? "Sign-in failed.";
}

/** A readable message for a refused PIN change. */
export function pinChangeError(e: unknown): string {
  const err = asError(e);
  if (err.code === "PASSKEY_REQUIRED") {
    return "You signed in with your PIN. Changing security settings needs a passkey or email-link sign-in.";
  }
  if (err.code === "REVERIFY_REQUIRED") return "Confirm with your passkey first.";
  if (err.code === "DEVICE_NOT_APPROVED") return "Only an approved device can change the PIN.";
  return err.message ?? "Request failed.";
}

/** Why this session cannot change the PIN right now, or null when it can. */
export function pinChangeBlocker(v: { can_change: boolean }, authMethod: string | undefined): string | null {
  if (v.can_change) return null;
  if (authMethod === "pin") {
    return "You signed in with your PIN. To set, change or remove it, sign in with a passkey or an email link.";
  }
  return "Only an approved device can set the PIN (Settings → Devices).";
}

/** One activity line in words, e.g. "Signed in with PIN". */
export function activityLabel(a: { action: string; method: string | null }): string {
  switch (a.action) {
    case "login":
      if (a.method === "pin") return "Signed in with PIN";
      if (a.method === "passkey") return "Signed in with passkey";
      if (a.method === "magic_link") return "Signed in with email link";
      if (a.method === "password") return "Signed in with password";
      return "Signed in";
    case "password_login_failed":
      return "Wrong password";
    case "password_login_locked":
      return "Password sign-in refused (locked)";
    case "password_lockout":
      return "Sign-in locked after 5 wrong tries";
    case "signin_code_sent":
      return "Code emailed";
    case "signin_code_failed":
      return "Wrong emailed code";
    case "email_send_failed":
      return "Email could not be sent";
    case "password_set":
      return "Password set";
    case "password_changed":
      return "Password changed";
    case "password_reset":
      return "Password reset by email code";
    case "device_trusted":
      return "Device remembered";
    case "device_trust_removed":
      return "Device forgotten";
    case "invite_accepted":
      return "Invitation accepted, password set";
    case "account_disabled":
      return "Account disabled";
    case "account_enabled":
      return "Account enabled";
    case "reverify":
      return "Confirmed with passkey";
    case "pin_login_failed":
      return "Wrong PIN";
    case "pin_login_locked":
      return "PIN sign-in refused (locked)";
    case "pin_lockout":
      return "PIN sign-in locked after 5 wrong PINs";
    case "pin_set":
      return "PIN set";
    case "pin_changed":
      return "PIN changed";
    case "pin_removed":
      return "PIN removed";
    case "device_approved":
      return "Device approved";
    case "device_revoked":
      return "Device revoked";
    case "passkey_added":
      return "Passkey added";
    case "passkey_deleted":
      return "Passkey deleted";
    default:
      return a.action;
  }
}

/** Warn-toned activity (shown highlighted). */
export function activityIsWarning(action: string): boolean {
  return [
    "pin_login_failed",
    "pin_login_locked",
    "pin_lockout",
    "password_login_failed",
    "password_login_locked",
    "password_lockout",
    "signin_code_failed",
    "email_send_failed",
  ].includes(action);
}

/** `DocChanged.doc_type` of the security notices (crates/api/src/pin.rs `NOTICE_DOC_TYPE`). */
export const SECURITY_NOTICE_DOC_TYPE = "security_notice";

/** The toast for a security notice (`doc_id` names the kind), or null for unknown kinds. */
export function securityNotice(
  kind: string,
): { tone: "warn" | "danger"; title: string; body: string } | null {
  if (kind === "pin_login") {
    return {
      tone: "warn",
      title: "Security",
      body: "Someone signed in to TradePrizm with your PIN. If it was not you, change the PIN and revoke the device in Settings → Devices.",
    };
  }
  if (kind === "pin_lockout") {
    return {
      tone: "danger",
      title: "Security",
      body: "PIN sign-in is locked after 5 wrong PINs. If that was not you, change the PIN in Settings → Security.",
    };
  }
  // Email + password sign-in (ADR-0051).
  if (kind === "new_device_signin") {
    return {
      tone: "warn",
      title: "Security",
      body: "A new device signed in to TradePrizm with your password and an emailed code. If it was not you, change your password and revoke the device in Settings → Devices.",
    };
  }
  if (kind === "password_lockout") {
    return {
      tone: "danger",
      title: "Security",
      body: "Sign-in is locked after 5 wrong passwords or codes. If that was not you, change your password in Settings → Security.",
    };
  }
  if (kind === "guest_lockout") {
    return {
      tone: "warn",
      title: "Security",
      body: "A guest account's sign-in was locked after 5 wrong tries (Settings → Users).",
    };
  }
  if (kind === "password_changed" || kind === "password_reset") {
    return {
      tone: "warn",
      title: "Security",
      body:
        kind === "password_reset"
          ? "Your TradePrizm password was reset with an emailed code; remembered devices must enter a code again. If it was not you, reset it now and check Settings → Devices."
          : "Your TradePrizm password was changed. If it was not you, reset it now and check Settings → Devices.",
    };
  }
  return null;
}

/**
 * Offer "Create a passkey on this device" after a PIN sign-in: only in a PIN session, where
 * passkeys work at this address, the browser supports them, and the offer was not dismissed here.
 */
export function shouldOfferPasskey(o: {
  authMethod: string | undefined;
  passkeysHere: boolean;
  supported: boolean;
  dismissed: boolean;
}): boolean {
  return o.authMethod === "pin" && o.passkeysHere && o.supported && !o.dismissed;
}

/** ⚙ A security notice older than this is not toasted (it is replayed history). */
export const SECURITY_NOTICE_MAX_AGE_MS = 2 * 60_000;

/**
 * Should this notice be toasted now? The trading stream replays its ring on every (re)connect,
 * so the same notice (kind + version, the server's millisecond timestamp) can arrive again, and a
 * freshly opened tab receives old ones. Toast each once per page, and only while it is recent.
 * `seen` is updated.
 */
export function freshSecurityNotice(
  kind: string,
  version: number,
  nowMs: number,
  seen: Set<string>,
): boolean {
  const key = `${kind}:${String(version)}`;
  if (seen.has(key)) return false;
  seen.add(key);
  if (seen.size > 256) seen.clear();
  return nowMs - version <= SECURITY_NOTICE_MAX_AGE_MS;
}
